import React, { useEffect, useState, useMemo } from "react";
import { Input, Table, TableProps, DatePicker, message, Row, Col, Select } from "antd";
import type { Dayjs } from "dayjs";
import { IReferredUser } from "./types";
import { getColumns } from "./Columns";
import { InvoiceService, GetReferralListResponse } from "../../../services/Invoices/InvoiceService/InvoiceService";
import PageWrapper from "../../../Layout/PageWrapper";
import useDebounce from "../../../hooks/useDebounce";
import type { SelectProps } from 'antd';

const { RangePicker } = DatePicker;

type RangeValue = [Dayjs | null, Dayjs | null] | null;

const ReferUserList: React.FC = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const [data, setData] = useState<IReferredUser[]>([]);
  const [filteredData, setFilteredData] = useState<IReferredUser[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [searchValue, setSearchValue] = useState("");
  const [dateRange, setDateRange] = useState<RangeValue>(null);
  const [selectedReferralCode, setSelectedReferralCode] = useState<string | undefined>();
  const [referrerList, setReferrerList] = useState<SelectProps['options']>([]);

  const [params, setParams] = useState({
    pageNumber: 1,
    pageSize: 20,
    sortField: "",
    sortType: ""
  });

  const debouncedSearchTerm = useDebounce(searchValue, 500);

  // Smart sorting
  const compareValues = (a: any, b: any, order: string) => {
    if (a == null) return 1;
    if (b == null) return -1;

    const aDate = new Date(a);
    const bDate = new Date(b);
    if (!isNaN(aDate.getTime()) && !isNaN(bDate.getTime())) {
      return order === "asc" ? aDate.getTime() - bDate.getTime() : bDate.getTime() - aDate.getTime();
    }

    if (!isNaN(Number(a)) && !isNaN(Number(b))) {
      return order === "asc" ? Number(a) - Number(b) : Number(b) - Number(a);
    }

    return order === "asc" ? String(a).localeCompare(String(b)) : String(b).localeCompare(String(a));
  };

  // Filtering logic
  const updateFilteredData = useMemo(() => {
    let filtered = [...data];

    // Search filter
    if (debouncedSearchTerm) {
      const lowerSearch = debouncedSearchTerm.toLowerCase();
      filtered = filtered.filter(item =>
        item.name?.toLowerCase().includes(lowerSearch) ||
        item.registeredEmail?.toLowerCase().includes(lowerSearch) ||
        item.referralCode?.toLowerCase().includes(lowerSearch) ||
        item.businessName?.toLowerCase().includes(lowerSearch)
      );
    }

    // Date range filter
    if (dateRange && dateRange[0] && dateRange[1]) {
      const startDate = dateRange[0].startOf("day").toDate();
      const endDate = dateRange[1].endOf("day").toDate();
      filtered = filtered.filter(item => {
        const itemDate = new Date(item.createdDate);
        return itemDate >= startDate && itemDate <= endDate;
      });
    }

    setTotalRecords(filtered.length);
    setFilteredData(filtered);
    return filtered;
  }, [data, debouncedSearchTerm, dateRange]);

  const tableData = useMemo(() => {
    let sorted = [...filteredData];

    if (params.sortField && params.sortType) {
      sorted.sort((a, b) => compareValues(
        a[params.sortField as keyof IReferredUser],
        b[params.sortField as keyof IReferredUser],
        params.sortType!
      ));
    }

    const start = (params.pageNumber - 1) * params.pageSize;
    return sorted.slice(start, start + params.pageSize);
  }, [filteredData, params]);

  // Fetch referrers on mount
  useEffect(() => {
    fetchReferrers();
  }, []);

  // Fetch referred users when referrer selected
  useEffect(() => {
    fetchData();
  }, [selectedReferralCode]);

  // Reset pagination on filter change
  useEffect(() => {
    setParams(prev => ({ ...prev, pageNumber: 1 }));
  }, [updateFilteredData]);

  const fetchReferrers = async () => {
    try {
      const res = await InvoiceService.getReferralList({ pageNumber: 1, pageSize: 1000 });
      const options: SelectProps['options'] = (res.data?.data || []).map((item) => ({
        value: item.referralCode,
        label: `${item.name} (${item.referralCode})` as string,
      }));
      setReferrerList(options);
    } catch (error) {
      console.error('Failed to fetch referrers:', error);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await InvoiceService.getReferredUsers(selectedReferralCode);
      setData(res?.data || []);
    } catch (error: any) {
      message.error(error?.response?.data?.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const handleTableChange: TableProps<IReferredUser>["onChange"] = (
    pagination,
    _,
    sorter
  ) => {
    let sortField = "";
    let sortType = "";

    if (!Array.isArray(sorter)) {
      sortField = sorter.field as string;
      sortType = sorter.order === "ascend" ? "asc" : sorter.order === "descend" ? "desc" : "";
    }

    setParams({
      ...params,
      pageNumber: pagination.current || 1,
      pageSize: pagination.pageSize || 20,
      sortField,
      sortType
    });
  };

  const onDateRangeChange = (dates: RangeValue) => {
    setDateRange(dates);
  };

  return (
    <PageWrapper title="Referred User List">
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={8}>
          <Select
            placeholder="Filter by Referrer (Name + Code)"
            options={referrerList}
            value={selectedReferralCode}
            onChange={setSelectedReferralCode}
            style={{ width: '100%' }}
            allowClear
            showSearch
          />
        </Col>
        <Col span={8}>
          <Input
            placeholder="Search users (name, email, code, business)"
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            style={{ width: '100%' }}
          />
        </Col>
        <Col span={8}>
          <RangePicker
            value={dateRange}
            onChange={onDateRangeChange}
            style={{ width: '100%' }}
            placeholder={['Start Date', 'End Date']}
          />
        </Col>
      </Row>

      <Table<IReferredUser>
        loading={loading}
        columns={getColumns()}
        dataSource={tableData}
        pagination={{
          current: params.pageNumber,
          pageSize: params.pageSize,
          total: totalRecords,
          showSizeChanger: true,
          showQuickJumper: true
        }}
        onChange={handleTableChange}
        scroll={{ x: "max-content" }}
        rowKey="userId"
      />
    </PageWrapper>
  );
};

export default ReferUserList;

